<template>
  <div class="space-y-4">
    <p class="text-2xl font-medium">
      {{ t('enterNewTyreTreadDepths') }}
    </p>

    <div class="grid grid-cols-2 gap-4">
      <SelectField
        name="tyreConditionFront"
        :options="tyreConditionOptions"
        :label="t('tyreConditionFront')"
      />
      <SelectField
        name="tyreConditionRear"
        :options="tyreConditionOptions"
        :label="t('tyreConditionRear')"
      />
    </div>

    <Divider />

    <div class="flex justify-end">
      <CVButton
        :disabled="!isFormSomeFieldDirty"
        variant="default"
        :is-loading="isUpdatingCar"
        @click.prevent="submit"
      >
        {{ t('updateTyreCondition') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Car } from '@/entities/cars/car.entity';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import Divider from '@/components/Divider.vue';
import { useForm } from 'vee-validate';
import { useUpdateCar } from '@/queries/use-cars';
import * as yup from 'yup';
import SelectField from '@/components/SelectField.vue';
import { TyreCondition } from '@carvia/ros-client-types';

const props = defineProps<{
  car: Car;
}>();

const emit = defineEmits<{
  (e: 'complete'): void;
}>();

const { t } = useI18n();

const tyreConditionOptions = computed(() =>
  Object.values(TyreCondition).map((condition) => ({
    label: t(`tyreCondition.${condition}`),
    value: condition,
  })),
);

const { mutateAsync: updateCar, isPending: isUpdatingCar } = useUpdateCar();

const { handleSubmit, isFieldDirty } = useForm<{
  tyreConditionFront?: TyreCondition;
  tyreConditionRear?: TyreCondition;
}>({
  initialValues: {
    tyreConditionFront: props.car.tyreConditionFront,
    tyreConditionRear: props.car.tyreConditionRear,
  },
  validationSchema: yup.object({
    tyreConditionFront: yup.string().required(),
    tyreConditionRear: yup.string().required(),
  }),
});

const isFormSomeFieldDirty = computed(() => {
  return isFieldDirty('tyreConditionFront') || isFieldDirty('tyreConditionRear');
});

const submit = handleSubmit(async (values) => {
  await updateCar({
    id: props.car.id,
    car: values,
  });
  emit('complete');
});
</script>

<i18n lang="json">
{
  "en": {
    "enterNewTyreTreadDepths": "Enter new tyre tread depths",
    "tyreConditionFront": "Front tyre tread depth",
    "tyreConditionRear": "Rear tyre tread depths",
    "updateTyreCondition": "Update tyre tread depths"
  },
  "de": {
    "enterNewTyreTreadDepths": "Neue Reifenprofiltiefen eingeben",
    "tyreConditionFront": "Vorderreifen Profiltiefe",
    "tyreConditionRear": "Hinterreifen Profiltiefe",
    "updateTyreCondition": "Reifenprofile aktualisieren"
  },
  "es": {
    "enterNewTyreTreadDepths": "Introducir nuevo dibujo de neumáticos",
    "tyreConditionFront": "Dibujo neumáticos delanteros",
    "tyreConditionRear": "Dibujo neumáticos traseros",
    "updateTyreCondition": "Actualizar dibujo de neumáticos"
  }
}
</i18n>
